import { useId, useRef, type ReactNode } from 'react'
import { useBodyScrollLock, useModalFocusTrap } from '../../hooks/useModalFocusTrap'

export interface ModalDialogProps {
  open: boolean
  onClose: () => void
  /** Visible dialog title (rendered as h2). */
  title: string
  children: ReactNode
  /** Panel layout: centered card or bottom sheet on small screens. */
  variant?: 'center' | 'bottom-sheet'
  className?: string
  closeOnEscape?: boolean
  closeOnBackdrop?: boolean
  /** Extra classes for the scrollable body region inside the panel. */
  bodyClassName?: string
  footer?: ReactNode
}

const PANEL_BASE =
  'relative flex max-h-[min(92dvh,880px)] w-full flex-col bg-background-primary outline-none shadow-lg border border-border-primary'

export function ModalDialog({
  open,
  onClose,
  title,
  children,
  variant = 'center',
  className = '',
  closeOnEscape = true,
  closeOnBackdrop = true,
  bodyClassName = '',
  footer,
}: ModalDialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useModalFocusTrap({
    active: open,
    containerRef: dialogRef,
    onClose,
    closeOnEscape,
  })
  useBodyScrollLock(open)

  if (!open) return null

  const panelPosition =
    variant === 'bottom-sheet'
      ? 'max-w-[560px] rounded-t-2xl sm:max-h-[min(85vh,720px)] sm:rounded-2xl'
      : 'max-w-[480px] rounded-2xl sm:max-h-[min(85vh,720px)]'

  const overlayAlign =
    variant === 'bottom-sheet'
      ? 'items-end justify-center sm:items-center sm:p-4'
      : 'items-center justify-center p-4'

  return (
    <div
      className={`fixed inset-0 z-[100] flex ${overlayAlign}`}
      role="presentation"
    >
      {closeOnBackdrop ? (
        <button
          type="button"
          className="absolute inset-0 cursor-default border-0 bg-background-overlay/60 p-0 backdrop-blur-[2px]"
          aria-label="Close dialog"
          onClick={onClose}
        />
      ) : (
        <div
          role="presentation"
          aria-hidden="true"
          className="absolute inset-0 bg-background-overlay/70"
        />
      )}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${PANEL_BASE} ${panelPosition} ${className}`}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-secondary px-4 py-4 sm:px-6">
          <h2
            id={titleId}
            className="m-0 min-w-0 flex-1 font-display text-lg font-semibold text-text-primary-900 sm:text-xl"
          >
            {title}
          </h2>
        </div>
        <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 ${bodyClassName}`}>
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-border-secondary bg-background-primary px-4 py-4 sm:px-6">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}
